import * as THREE from 'three';
import { ActivityRing } from '../effects/ActivityRing';

export interface BlockData {
  height: number;
  hash: string;
  timestamp: number;
  transactionCount: number;
  totalFees: number;
  miner: string;
  size: number;
}

export interface OrbitalData {
  radius: number;
  angle: number;
  speed: number;
  inclination: number;
}

export interface SurfaceDetail {
  roughness: number;
  metalness: number;
  emissiveIntensity: number;
}

export interface PlanetActivity {
  dockingShips: Set<string>;
  surfaceGlow: number;
  activityRings: ActivityRing[];
}

export class BlockPlanet {
  public readonly blockHeight: number;
  public readonly mesh: THREE.Group;
  public readonly planetMesh: THREE.Mesh;
  public radius: number;
  public surfaceDetail: SurfaceDetail;
  public orbit: OrbitalData;
  public data: BlockData;
  public activity: PlanetActivity;
  
  private glowMesh: THREE.Mesh;
  private ringMeshes: THREE.Mesh[] = [];
  private rotationSpeed: number;

  constructor(blockData: BlockData, mesh: THREE.Mesh, radius: number) {
    this.blockHeight = blockData.height;
    this.data = blockData;
    this.radius = radius;
    
    // Create group to hold planet and effects
    this.mesh = new THREE.Group();
    this.planetMesh = mesh;
    this.mesh.add(this.planetMesh);
    
    // Initialize surface details
    this.surfaceDetail = {
      roughness: 0.8,
      metalness: 0.2,
      emissiveIntensity: 0.1
    };
    
    // Initialize orbital data
    this.orbit = {
      radius: 30,
      angle: 0,
      speed: 0.1,
      inclination: (Math.random() - 0.5) * 0.2 // Slight random tilt
    };
    
    // Initialize activity tracking
    this.activity = {
      dockingShips: new Set(),
      surfaceGlow: 0,
      activityRings: []
    };
    
    // Set rotation speed based on transaction count
    this.rotationSpeed = 0.1 + Math.log10(blockData.transactionCount + 1) * 0.05;
    
    // CRITICAL FIX: Set initial orbital position immediately to prevent (0,0,0) coordinates
    this.updateOrbitalPosition();
    
    // Create glow effect
    this.glowMesh = this.createGlowMesh();
    this.mesh.add(this.glowMesh);
    
    // Apply initial visual properties
    this.updateVisualProperties();
  }

  private createGlowMesh(): THREE.Mesh {
    const glowGeometry = new THREE.SphereGeometry(this.radius * 1.2, 32, 32);
    const glowMaterial = new THREE.ShaderMaterial({
      vertexShader: `
        varying vec3 vNormal;
        void main() {
          vNormal = normalize(normalMatrix * normal);
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform vec3 glowColor;
        uniform float intensity;
        varying vec3 vNormal;
        
        void main() {
          float glow = pow(0.7 - dot(vNormal, vec3(0, 0, 1.0)), 2.0);
          gl_FragColor = vec4(glowColor * glow, glow * intensity);
        }
      `,
      uniforms: {
        glowColor: { value: new THREE.Color(0x4488ff) },
        intensity: { value: 0.5 }
      },
      side: THREE.BackSide,
      blending: THREE.AdditiveBlending,
      transparent: true
    });
    
    return new THREE.Mesh(glowGeometry, glowMaterial);
  }

  public update(deltaTime: number): void {
    // Update orbital position
    this.orbit.angle += this.orbit.speed * deltaTime;
    this.updateOrbitalPosition();
    
    // Rotate planet
    this.planetMesh.rotation.y += this.rotationSpeed * deltaTime;
    
    // Update activity effects
    this.updateActivityEffects(deltaTime);
    
    // Update activity rings
    this.activity.activityRings = this.activity.activityRings.filter(ring => {
      ring.update(deltaTime);
      if (ring.isDead()) {
        ring.dispose();
        return false;
      }
      return true;
    });
  }

  private updateOrbitalPosition(): void {
    const x = Math.cos(this.orbit.angle) * this.orbit.radius;
    const z = Math.sin(this.orbit.angle) * this.orbit.radius;
    const y = Math.sin(this.orbit.inclination) * this.orbit.radius * 0.1;
    
    this.mesh.position.set(x, y, z);
  }

  private updateActivityEffects(deltaTime: number): void {
    // Fade surface glow
    this.activity.surfaceGlow = Math.max(0, this.activity.surfaceGlow - deltaTime * 0.5);
    
    // Update glow intensity
    if (this.glowMesh.material instanceof THREE.ShaderMaterial) {
      const baseIntensity = 0.3 + this.activity.dockingShips.size * 0.1;
      const totalIntensity = baseIntensity + this.activity.surfaceGlow;
      this.glowMesh.material.uniforms['intensity'].value = Math.min(1.0, totalIntensity);
    }
    
    // Update emissive intensity based on activity
    if (this.planetMesh.material instanceof THREE.MeshStandardMaterial) {
      this.planetMesh.material.emissiveIntensity = 
        this.surfaceDetail.emissiveIntensity + this.activity.surfaceGlow * 0.5;
    }
  }

  private updateVisualProperties(): void {
    if (this.planetMesh.material instanceof THREE.MeshStandardMaterial) {
      this.planetMesh.material.roughness = this.surfaceDetail.roughness;
      this.planetMesh.material.metalness = this.surfaceDetail.metalness;
      this.planetMesh.material.emissiveIntensity = this.surfaceDetail.emissiveIntensity;
    }
  }

  public addDockingShip(shipId: string): void {
    this.activity.dockingShips.add(shipId);
    this.flashActivity();
  }

  public removeDockingShip(shipId: string): void {
    this.activity.dockingShips.delete(shipId);
  }

  public flashActivity(): void {
    this.activity.surfaceGlow = 1.0;
    
    // Add new activity ring
    const ring = new ActivityRing(this.radius);
    this.activity.activityRings.push(ring);
    this.mesh.add(ring.mesh);
  }

  public setOrbit(radius: number, angle: number, speed: number): void {
    this.orbit.radius = radius;
    this.orbit.angle = angle;
    this.orbit.speed = speed;
    this.updateOrbitalPosition();
  }

  public getPosition(): THREE.Vector3 {
    return this.mesh.position.clone();
  }

  public dispose(): void {
    // Dispose of activity rings
    this.activity.activityRings.forEach(ring => ring.dispose());
    
    // Dispose of glow mesh
    if (this.glowMesh.geometry) {
      this.glowMesh.geometry.dispose();
    }
    if (this.glowMesh.material) {
      if (Array.isArray(this.glowMesh.material)) {
        this.glowMesh.material.forEach(mat => mat.dispose());
      } else {
        this.glowMesh.material.dispose();
      }
    }
    
    // Dispose of planet mesh
    if (this.planetMesh.geometry) {
      this.planetMesh.geometry.dispose();
    }
    if (this.planetMesh.material) {
      if (Array.isArray(this.planetMesh.material)) {
        this.planetMesh.material.forEach(mat => mat.dispose());
      } else {
        this.planetMesh.material.dispose();
      }
    }
    
    // Clear references
    this.activity.dockingShips.clear();
  }
}